import React, {useCallback, useEffect, useRef, useState} from 'react';

import {STATSIG_GATES} from 'src/contexts/statsig-contex';
import {selectAccessRights} from 'src/hooks/access-rights/services/access-rights-slice';
import {useAppSelector} from 'src/store/hooks';

import {Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {Tooltip} from '@pb/kit-component-popups';
import {useGateValue} from '@pb/kit-lib-statsig';

import {UserDetailsDrawer} from './user-details-drawer';

import type {UserDetails} from 'src/openapi/admin/models/UserDetails';

export const useUserDetailsDrawer = (
    enabled: boolean, onAccessChanged?: (userId: number, details: UserDetails) => void,
) => {
    const gate = useGateValue(STATSIG_GATES.ACCOUNT_PROVISIONING_HISTORY);
    const rights = useAppSelector(selectAccessRights);
    const allowed = enabled && gate && !!rights?.includes('USER_SCREEN_VIEW');
    const [userId, setUserId] = useState<number | null>(null);
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const tableRef = useRef<HTMLElement | null>(null);
    const getRefTable = useCallback((element: HTMLElement | null) => {
        tableRef.current = element;
    }, []);
    const close = useCallback(() => setOpen(false), []);
    useEffect(() => {
        if (!allowed) {
            close();
        }
    }, [allowed, close]);
    useEffect(() => {
        if (open || userId === null) {
            return;
        }
        const timer = window.setTimeout(() => {
            setUserId(null);
            const target = triggerRef.current?.isConnected ? triggerRef.current : tableRef.current;
            if (target) {
                if (target === tableRef.current) {
                    target.tabIndex = -1;
                }
                target.focus({preventScroll: true});
            }
        }, 300);
        return () => window.clearTimeout(timer);
    }, [open, userId]);

    const renderAction = (id: number) => allowed ? (
        <Tooltip anchor={({anchorRef, getAnchorProps}) => (
            <BasicIconButton
                iconProps={{type: Icons.COGS, size: 'Size_12'}}
                {...getAnchorProps()}
                ref={anchorRef}
                aria-label="View user details"
                aria-haspopup="dialog"
                aria-expanded={Boolean(open && userId === id)}
                onClick={event => {
                    event.stopPropagation();
                    triggerRef.current = event.currentTarget;
                    setUserId(id);
                    setOpen(true);
                }}
            />
        )}>View user details
        </Tooltip>
    ) : null;

    const drawer = allowed && userId !== null ? (
        <UserDetailsDrawer
            key={userId}
            userId={userId}
            open={open}
            onClose={close}
            onAccessChanged={details => onAccessChanged?.(userId, details)}
            canViewProducts={!!rights?.some(right => ['USER_PRODUCTS_VIEW', 'USER_PRODUCTS_EDIT'].includes(right))}
            canViewLicense={!!rights?.includes('LICENSE_SCREEN_VIEW')}
        />
    ) : null;
    return {allowed, selectedUserId: open ? userId : null, getRefTable, renderAction, drawer};
};
